import type { Simplify } from "type-fest";
import { v4 } from "uuid";

import {
  CuratedPlaceOfSafetySchema,
  dayjs,
  pickRandom,
  SAMPLE_FORENAMES,
  SAMPLE_SURNAMES,
  type CuratedPlaceOfSafety,
  type CuratedPlaceOfSafetyUnbranded,
  type Dayjs,
} from "@thalamos/common";

import { LocationSchema, type LocationUnbranded } from "../types/location.js";
import {
  WorkflowVersionSchema,
  type WorkflowVersion,
} from "../types/workflowVersion.js";

type SectionData = Record<string, unknown>;

export type PersonName = { forename: string; surname: string };

export type QuickCreateSampleData = {
  createIncidentMain: SectionData;
  triageMain: SectionData;
  personDetails: SectionData;
  detentionDetails: SectionData;
  informationForHealthcare: SectionData;
  recordPlaceOfSafetyMain: SectionData;
  recordPlaceOfSafetyArrival: SectionData;
};

/**
 * Optional context for generating quick-create sample data.
 *
 * - `now` — anchor for all relative date/times (defaults to current time)
 * - `curatedPlaceOfSafety` — if provided, uses this curated POS instead of
 *   a fabricated ad-hoc one, making the created incident appear more realistic.
 * - `name` — if provided, names the person instead of picking a random name.
 *   The same name is used everywhere the person is recorded.
 */
export type QuickCreateSampleDataOptions = {
  now?: Dayjs;
  curatedPlaceOfSafety?: CuratedPlaceOfSafety;
  name?: PersonName;
};

/**
 * A dispatch number in the shape forces actually use: bare digits, no prefix.
 * Short enough to collide, which quick-created incidents tolerate.
 */
export function generateDemoDispatchNumber(): string {
  const digits = 4 + Math.floor(Math.random() * 3);
  const smallest = 10 ** (digits - 1);
  return String(smallest + Math.floor(Math.random() * smallest * 9));
}

// -- Shared building blocks --

function buildSharedCreateIncidentBase(name: PersonName): SectionData {
  return {
    // Fixed rather than generated, so this sample data stays deterministic.
    dispatchNumber: { value: "123456" },
    commandUnit: { value: v4(), valueLabel: "Test Command Unit" },
    personForename: { value: name.forename, isUnknown: false },
    personSurname: { value: name.surname, isUnknown: false },
  };
}

function buildSharedPersonDetails(name: PersonName): SectionData {
  return {
    ...buildPersonIdentity(name),
    satisfiedPersonIsOver18: null,
  };
}

/** The person-details fields the 136 and MCA forms record identically. */
function buildPersonIdentity(name: PersonName): SectionData {
  return {
    personForename: { value: name.forename, isUnknown: false },
    personSurname: { value: name.surname, isUnknown: false },
    dateOfBirth: { value: "1990-01-01", isUnknown: false },
    personAddress: {
      value: LocationSchema.parse({
        primaryLocationType: "postalAddress",
        userProvided: { formattedValue: "123 High St" },
        postalAddress: [
          {
            shortName: "123 High St",
            longName: "123 High Street",
            types: ["str"],
          },
        ],
      } satisfies LocationUnbranded),
      isUnknown: false,
    },
    confirmPersonAddress: { value: "yes", valueLabel: "Yes" },
    gender: { value: "preferNotToSay", valueLabel: "Prefer not to say" },
    ethnicAppearance: { value: "IC1", valueLabel: "White" },
    selfDeclaredEthnicity: {
      value: "whiteEnglish",
      valueLabel:
        "White - English / Welsh / Scottish / Northern Irish / British",
    },
    selfDeclaredEthnicityOther: null,
    hasRelativeOrFriend: { value: "no", valueLabel: "No" },
    relativeOrFriendForename: null,
    relativeOrFriendSurname: null,
    relativeOrFriendRelationship: null,
    relativeOrFriendSameAddress: null,
    relativeOrFriendAddress: null,
    relativeOrFriendTelephoneNumber: null,
    relativeOrFriendInformed: null,
    reasonNotInformed: null,
  };
}

function buildSharedDetentionDetailsBase(now: Dayjs): SectionData {
  return {
    // A number recorded against a detention held anywhere but custody keeps the
    // custody question on screen for one that never was, so a scenario held in
    // custody sets its own rather than inheriting one from here.
    custodyNumber: null,
    dateTimeOfDetention: {
      value: now.subtract(2, "hour").toISOString(),
    },
    locationDetentionTookPlace: {
      value: LocationSchema.parse({
        primaryLocationType: "userProvided",
        userProvided: { formattedValue: "High Street" },
      }),
    },
    orgResponsibleForDetention: {
      value: v4(),
      valueLabel: "Test Organisation",
    },
  };
}

const sharedInformationForHealthcare: SectionData = {
  incidentDescription: { value: "Incident description" },
  wasHealthcareProvided: { value: "no", valueLabel: "No" },
  medicalAttentionProvided: null,
  wasForceUsed: { value: "no", valueLabel: "No" },
  useOfForceType: null,
  useOfForceContextHandcuffing: null,
  useOfForceContextHandcuffingOther: null,
  useOfForceContextUnarmedSkills: null,
  useOfForceContextUnarmedSkillsOther: null,
  useOfForceContextDogs: null,
  useOfForceContextDogsOther: null,
  useOfForceContextBaton: null,
  useOfForceContextBatonOther: null,
  useOfForceContextIrritantSpray: null,
  useOfForceContextIrritantSprayOther: null,
  useOfForceContextLimbBodyRestraints: null,
  useOfForceContextLimbBodyRestraintsOther: null,
  useOfForceContextSpitGuard: null,
  useOfForceContextSpitGuardOther: null,
  useOfForceContextShield: null,
  useOfForceContextShieldOther: null,
  useOfForceContextCed: null,
  useOfForceContextCedOther: null,
  useOfForceContextAep: null,
  useOfForceContextAepOther: null,
  useOfForceContextFirearms: null,
  useOfForceContextFirearmsOther: null,
  useOfForceContextOtherImprovised: null,
  useOfForceContextOtherImprovisedOther: null,
  hasPersonBeenSearched: { value: "no", valueLabel: "No" },
  didSeizeProperty: null,
  detailsPropertySeized: null,
  hasAdditionalRiskFactors: { value: "no", valueLabel: "No" },
  describeRiskFactors: null,
  describeRiskFactorsOther: null,
};

const posTypeLabels: Record<string, string> = {
  hbPos136: "HBPOS (136 Suite)",
  emergencyDepartment: "Emergency Department (A&E)",
  returnedToMentalHealthUnit: "Returned to Mental Health Unit",
  careHome: "Care Home",
  custody: "Custody (exceptional circumstances)",
  privateHome: "Private Home",
  other: "Other",
};

function buildPlaceOfSafetyMain(
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
): SectionData {
  // Always use "hbPos136" as the placeOfSafetyType regardless of the curated
  // POS's actual posType. Choosing a different type (e.g. "careHome", "custody")
  // would make conditional fields like whyNotHbPosReason or
  // whoAuthorisedCustodySuite visible and required, which the quick-create
  // sample data does not populate.
  if (curatedPlaceOfSafety) {
    return {
      placeOfSafety: curatedPlaceOfSafety,
      confirmPlaceOfSafetyCorrect: { value: "yes", valueLabel: "Yes" },
      placeOfSafetyType: {
        value: "hbPos136",
        valueLabel: posTypeLabels["hbPos136"],
      },
    };
  }

  // Fallback: fabricate a curated POS with synthetic data
  return {
    placeOfSafety: CuratedPlaceOfSafetySchema.parse({
      id: v4(),
      name: "Place of Safety",
      address: "Place of Safety Address",
      postalCode: "AB12 3CD",
      odsCode: "ODS1",
      organisationId: v4(),
      posType: "hbPos136",
    } satisfies CuratedPlaceOfSafetyUnbranded),
    confirmPlaceOfSafetyCorrect: { value: "yes", valueLabel: "Yes" },
    placeOfSafetyType: {
      value: "hbPos136",
      valueLabel: "HBPOS (136 Suite)",
    },
  };
}

function buildRecordPlaceOfSafetyArrival(now: Dayjs): SectionData {
  return {
    arrivalDateTime: {
      value: now.subtract(30, "minute").toISOString(),
    },
    didNotArrive: null,
    wasEntryDelayed: { value: "no", valueLabel: "No" },
    whyWasEntryDelayed: null,
    whyWasEntryDelayedOther: null,
    howLongWasEntryDelayed: null,
    methodOfTransport: {
      value: "policeVehicle",
      valueLabel: "Police vehicle",
    },
    whyMethodOfTransportPoliceVehicle: {
      value: "ambulanceAvailableInTimeframe",
      valueLabel: "Ambulance not available within agreed timeframe",
    },
    whyMethodOfTransportPoliceVehicleOther: null,
    methodOfTransportOther: null,
  };
}

function buildExtendedTriageMain(reasonNotConsulted: {
  value: string;
  valueLabel: string;
}): SectionData {
  return {
    isHealthcareProfessionalConsulted: { value: "no", valueLabel: "No" },
    howDidYouContactNhs: null,
    howDidYouContactNhsOtherDetails: null,
    reasonNotConsulted,
    givenHealthCareProfessionalAdvice: null,
    reasonsForDetention: { value: "Reasons for detention" },
    voluntaryAction: null,
    voluntaryActionOtherDetails: null,
    whatWillYouDo: { value: "136", valueLabel: "136" },
    whatWillYouDoOther: null,
  };
}

// -- met-1.0 --

function buildMetData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: {
        value: now.subtract(2, "hour").subtract(30, "minute").toISOString(),
      },
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "immediateRestraint",
        valueLabel:
          "There was an immediate need to restrain the person to protect them or others",
      }),
      clinicalCaseRecordNumber: null,
      confirmMentalCapacityLack: null,
      otherAction: null,
      otherActionDetails: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
      confirmPersonAddress: null,
      londonBoroughDetentionTookPlace: {
        value: "islington",
        valueLabel: "Islington",
      },
      locationType: { value: "street", valueLabel: "Street" },
      confirmCustodySuite: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
      incidentLocation: null,
      commandUnit: null,
      londonBoroughIncidentTookPlace: null,
      locationTypeIncident: null,
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
      isUnderInfluence: { value: "no", valueLabel: "No" },
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- btp (all versions) --

function buildBtpData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: { value: now.format("YYYY-MM-DD") },
      geographicUnit: { value: v4(), valueLabel: "Test Geographic Unit" },
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "violent",
        valueLabel: "Patient is violent",
      }),
      explainWhyActionDiffers: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      locationType: { value: "concourse", valueLabel: "Concourse" },
      locationTypeOther: null,
      geographicUnit: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- colp-1.0 --

function buildColpData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: {
        value: now.subtract(2, "hour").subtract(30, "minute").toISOString(),
      },
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "immediateRestraint",
        valueLabel:
          "There was an immediate need to restrain the person to protect them or others",
      }),
      clinicalCaseRecordNumber: null,
      confirmMentalCapacityLack: null,
      otherAction: null,
      otherActionDetails: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
      confirmPersonAddress: null,
      londonBoroughDetentionTookPlace: {
        value: "islington",
        valueLabel: "Islington",
      },
      locationType: { value: "street", valueLabel: "Street" },
      confirmCustodySuite: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
      incidentLocation: null,
      commandUnit: null,
      londonBoroughIncidentTookPlace: null,
      locationTypeIncident: null,
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
      isUnderInfluence: { value: "no", valueLabel: "No" },
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- Lookup --

type WorkflowDataBuilder = (
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
) => QuickCreateSampleData;

const builderByWorkflow: Record<WorkflowVersion, WorkflowDataBuilder> = {
  [WorkflowVersionSchema.enum.MET_1_0]: buildMetData,
  [WorkflowVersionSchema.enum.BTP_1_0]: buildBtpData,
  [WorkflowVersionSchema.enum.BTP_1_1]: buildBtpData,
  [WorkflowVersionSchema.enum.BTP_1_2]: buildBtpData,
  [WorkflowVersionSchema.enum.COLP_1_0]: buildColpData,
};

function nameOrRandom(name: PersonName | undefined): PersonName {
  return (
    name ?? {
      forename: pickRandom(SAMPLE_FORENAMES),
      surname: pickRandom(SAMPLE_SURNAMES),
    }
  );
}

export function getQuickCreateSampleData(
  workflowVersion: string,
  options: QuickCreateSampleDataOptions = {},
): QuickCreateSampleData {
  const now = options.now ?? dayjs.utc();
  const builder =
    builderByWorkflow[workflowVersion as WorkflowVersion] ?? buildMetData;
  return builder(now, options.curatedPlaceOfSafety, nameOrRandom(options.name));
}

// -- Mental Capacity Act (all workflows) --

export type QuickCreateMcaSampleData = Simplify<
  Pick<
    QuickCreateSampleData,
    | "createIncidentMain"
    | "triageMain"
    | "recordPlaceOfSafetyMain"
    | "recordPlaceOfSafetyArrival"
  > & {
    mcaPersonDetails: SectionData;
    mcaIncidentDetails: SectionData;
    mcaCapacityAssessment: SectionData;
    mcaAccompaniment: SectionData;
  }
>;

function buildMcaIncidentDetailsBase(now: Dayjs): SectionData {
  return {
    dateTimeOfIncident: { value: now.subtract(2, "hour").toISOString() },
    incidentLocation: {
      value: LocationSchema.parse({
        primaryLocationType: "userProvided",
        userProvided: { formattedValue: "High Street" },
      } satisfies LocationUnbranded),
    },
    bcuIncidentTookPlace: { value: v4(), valueLabel: "Test Command Unit" },
    // Any location type that is not custody, which would ask for the custody suite.
    locationType: { value: "homeAddress", valueLabel: "Home Address" },
    custodyNumber: null,
  };
}

/**
 * The person lacks capacity, so the assessment runs to the end rather than
 * exiting early, and the officer may act in their best interests.
 */
const mcaCapacityAssessment: SectionData = {
  assessingIndividual: {
    value: "reportingOfficer",
    valueLabel: "Reporting Officer",
  },
  ambulanceStaffName: null,
  otherAssessor: null,
  decisionUnableToMake: { value: "Whether to accept medical treatment" },
  canUnderstandInformation: { value: "no", valueLabel: "No" },
  canUnderstandInformationReasoning: {
    value: "Unable to process complex information",
  },
  canRetainInformation: { value: "no", valueLabel: "No" },
  canRetainInformationReasoning: {
    value: "Forgets information within minutes",
  },
  canWeighInformation: { value: "no", valueLabel: "No" },
  canWeighInformationReasoning: { value: "Cannot weigh consequences" },
  canUseInformation: { value: "yes", valueLabel: "Yes" },
  canUseInformationReasoning: { value: "Can use information" },
  canCommunicateDecision: { value: "yes", valueLabel: "Yes" },
  canCommunicateDecisionReasoning: { value: "Can communicate verbally" },
  impairmentDisturbance: { value: "yes", valueLabel: "Yes" },
  impairmentType: { value: "Confusion due to medical condition" },
  earlyExitCapable: null,
  earlyExitNoImpairment: null,
  lackOfCapacity: { value: "yes", valueLabel: "Yes" },
  earlyExitNoLackOfCapacity: null,
  reasoningLackOfCapacity: { value: "Based on the above assessment" },
  bestInterestSteps: { value: "Consulted with family and medical staff" },
  bestInterestPhysicalHealthCondition: { value: "yes", valueLabel: "Yes" },
  bestInterestPhysicalConditionDescription: {
    value: "Hypoglycaemia requiring emergency glucose administration",
  },
  bestInterestExceptionalCircumstances: null,
  bestInterestAct: { value: "Transport to hospital for treatment" },
  restraintNecessary: {
    value: "noRestraintUsed",
    valueLabel: "No restraint used",
  },
  restraintRiskReasoning: { value: "Person was cooperative throughout" },
  useOfForceType: null,
};

/** Accompanied, because an incident that is not goes to finish rather than share. */
const mcaAccompaniment: SectionData = {
  accompanyingToEmergencyDepartment: { value: "yes", valueLabel: "Yes" },
  notAccompanyingReason: null,
  personLeftInCareOf: null,
};

/**
 * An MCA incident's place of safety is always an Emergency Department, and the
 * form does not ask which type of place it is.
 */
function buildMcaPlaceOfSafetyMain(
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
): SectionData {
  return {
    placeOfSafety:
      curatedPlaceOfSafety ??
      CuratedPlaceOfSafetySchema.parse({
        id: v4(),
        name: "Emergency Department",
        address: "Emergency Department Address",
        postalCode: "AB12 3CD",
        odsCode: "ODS1",
        organisationId: v4(),
        posType: "emergencyDepartment",
      } satisfies CuratedPlaceOfSafetyUnbranded),
    confirmPlaceOfSafetyCorrect: { value: "yes", valueLabel: "Yes" },
    placeOfSafetyType: null,
  };
}

type McaWorkflowFields = {
  whatWillYouDoLabel: string;
  mcaIncidentDetails: SectionData;
  recordPlaceOfSafetyMain: SectionData;
};

const londonMcaFields: McaWorkflowFields = {
  whatWillYouDoLabel: "Mental Capacity Act",
  mcaIncidentDetails: {
    londonBoroughIncidentTookPlace: {
      value: "islington",
      valueLabel: "Islington",
    },
    confirmCustodySuite: null,
  },
  recordPlaceOfSafetyMain: {
    whyTakingPersonToEmergencyDepartment: null,
    otherEmergencyDepartmentReasonDetails: null,
  },
};

const btpMcaFields: McaWorkflowFields = {
  whatWillYouDoLabel: "Section 1-6 (Mental Capacity Act)",
  mcaIncidentDetails: {
    custodySuiteName: null,
    policeForceResponsibleForCustodySuite: null,
  },
  recordPlaceOfSafetyMain: {
    placeOfSafetyTypeOther: null,
    whoAuthorisedCustodySuite: null,
    whyNotHbPosReason: null,
    whyNotHbPosReasonOther: null,
  },
};

const mcaFieldsByWorkflow: Record<WorkflowVersion, McaWorkflowFields> = {
  [WorkflowVersionSchema.enum.MET_1_0]: londonMcaFields,
  [WorkflowVersionSchema.enum.BTP_1_0]: btpMcaFields,
  [WorkflowVersionSchema.enum.BTP_1_1]: btpMcaFields,
  [WorkflowVersionSchema.enum.BTP_1_2]: btpMcaFields,
  [WorkflowVersionSchema.enum.COLP_1_0]: londonMcaFields,
};

/**
 * Sample data for an MCA incident recorded up to and including arrival at the
 * Emergency Department, so that it is ready to share.
 *
 * The create-incident and triage sections are the 136 ones with MCA chosen in
 * triage, so the two incident types start from the same record.
 */
export function getQuickCreateMcaSampleData(
  workflowVersion: WorkflowVersion,
  options: QuickCreateSampleDataOptions = {},
): QuickCreateMcaSampleData {
  const now = options.now ?? dayjs.utc();
  const name = nameOrRandom(options.name);
  const section136Data = getQuickCreateSampleData(workflowVersion, {
    now,
    name,
  });
  const workflowFields = mcaFieldsByWorkflow[workflowVersion];

  return {
    createIncidentMain: section136Data.createIncidentMain,
    triageMain: {
      ...section136Data.triageMain,
      whatWillYouDo: {
        value: "mca",
        valueLabel: workflowFields.whatWillYouDoLabel,
      },
    },
    mcaPersonDetails: {
      ...buildPersonIdentity(name),
      satisfiedPersonIsOver16: null,
    },
    mcaIncidentDetails: {
      ...buildMcaIncidentDetailsBase(now),
      ...workflowFields.mcaIncidentDetails,
    },
    mcaCapacityAssessment,
    mcaAccompaniment,
    recordPlaceOfSafetyMain: {
      ...buildMcaPlaceOfSafetyMain(options.curatedPlaceOfSafety),
      ...workflowFields.recordPlaceOfSafetyMain,
    },
    recordPlaceOfSafetyArrival: section136Data.recordPlaceOfSafetyArrival,
  };
}
